import { Typography, TextField, IconButton, InputAdornment, useMediaQuery } from '@mui/material';
import { Stack, Box } from '@mui/system';
import { FC, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';

import { Button } from 'src/components/button';
import { ReactComponent as WarningIcon } from 'src/assets/svg/warning.svg';
import { ReactComponent as Line } from 'src/assets/svg/line.svg';
import { ReactComponent as VSLogo } from 'src/assets/svg/vita-logo.svg';
import { ReactComponent as HoSLogoWhite } from 'src/assets/svg/hos-logo-white.svg';
import { ReactComponent as HoSLogoBlack } from 'src/assets/svg/hos-logo-black.svg';
import { getTheme } from 'src/utils/get-theme';
import { routes } from 'src/constants/routes';

import { setNewPasswordDataSchema } from '../schema/setNewPasswordSchema';
import { SetNewPasswordFormValues } from '../types';
import { setNewPasswordDataKeys } from '../constants';
import { ReactComponent as ShowPasswordIcon } from '../../../assets/svg/show-pass.svg';
import { useUpdateUserPasswordSupabase } from '../hooks/use-update-user-password-supabase';

import { styles } from './styles/otp-form';

export const SupabasePasswordResetForm: FC = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { theme } = getTheme();
    const isSmallScreen = useMediaQuery('(max-width:1023px)');

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const {
        mutate: updatePassword,
        isLoading: isUpdatingPassword,
        error: updatePasswordError,
        isSuccess,
    } = useUpdateUserPasswordSupabase();

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<SetNewPasswordFormValues>({
        resolver: yupResolver(setNewPasswordDataSchema),
        mode: 'onSubmit',
    });

    const onSubmit = (data: SetNewPasswordFormValues) => {
        updatePassword({
            password: data[setNewPasswordDataKeys.newPassword],
        });
    };

    const getErrorMessage = (): string | null => {
        if (!updatePasswordError) return null;

        switch (updatePasswordError.status) {
            case 422:
                return updatePasswordError.message;
            default:
                return t('supabase_password_login_error_generic');
        }
    };

    return (
        <Box css={styles.wrapper}>
            <Box css={styles.preview}>
                <Box css={styles.previewContainer}>
                    <h1 css={styles.welcomeTitle}>
                        <Trans i18nKey="supabase_welcome" />
                    </h1>

                    <h5 css={styles.subtitle}>
                        <Trans i18nKey="supabase_login_to_my_account" />
                    </h5>
                </Box>
                <Box css={styles.logos}>
                    <VSLogo width={48} height={48} />
                    <Line css={styles.line} />
                    <HoSLogoWhite />
                </Box>
            </Box>
            {isSmallScreen && (
                <Box css={styles.logos}>
                    <VSLogo width={48} height={48} />
                    <Line css={styles.line} />
                    <HoSLogoBlack />
                </Box>
            )}

            <Box css={styles.login}>
                <Box css={styles.loginContainer}>
                    <Stack css={styles.card}>
                        {isSuccess ? (
                            <Box css={styles.content}>
                                <Box css={styles.headerBox}>
                                    <Typography variant="h2" css={styles.headerLineHeight}>
                                        {t('supabase_password_reset_submit')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.text.secondary}
                                    >
                                        {t('supabase_password_reset_success_message')}
                                    </Typography>
                                </Box>

                                <Button onClick={() => navigate(routes.index)} css={styles.button}>
                                    {t('supabase_password_reset_success_cta')}
                                </Button>
                            </Box>
                        ) : (
                            <Box css={styles.content}>
                                <Box css={styles.headerBox}>
                                    <Typography variant="h2" css={styles.headerLineHeight}>
                                        {t('supabase_reset_password_title')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color={theme.palette.text.secondary}
                                    >
                                        {t('supabase_reset_password_subtitle')}
                                    </Typography>
                                </Box>

                                {(getErrorMessage() ||
                                    errors.confirmNewPassword ||
                                    errors.newPassword) && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={1}
                                        css={styles.errorWrap}
                                    >
                                        <WarningIcon
                                            width={theme.typography.h2.fontSize}
                                            height={theme.typography.h2.fontSize}
                                            color={theme.palette.error.main}
                                        />
                                        <Typography color={theme.palette.error.main}>
                                            {getErrorMessage() ||
                                                t(
                                                    errors.newPassword?.message ||
                                                        errors.confirmNewPassword?.message ||
                                                        ''
                                                )}
                                        </Typography>
                                    </Stack>
                                )}

                                <form onSubmit={handleSubmit(onSubmit)} noValidate>
                                    <Box css={styles.inputsWrap}>
                                        <Controller
                                            name={setNewPasswordDataKeys.newPassword}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    variant="standard"
                                                    type={showPassword ? 'text' : 'password'}
                                                    placeholder={t('supabase_new_password')}
                                                    fullWidth
                                                    error={Boolean(errors.newPassword)}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() =>
                                                                        setShowPassword((v) => !v)
                                                                    }
                                                                >
                                                                    <ShowPasswordIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    css={styles.inputField}
                                                />
                                            )}
                                        />

                                        <Controller
                                            name={setNewPasswordDataKeys.confirmNewPassword}
                                            control={control}
                                            render={({ field }) => (
                                                <TextField
                                                    {...field}
                                                    variant="standard"
                                                    type={showConfirmPassword ? 'text' : 'password'}
                                                    placeholder={t('supabase_confirm_new_password')}
                                                    fullWidth
                                                    error={Boolean(errors.confirmNewPassword)}
                                                    InputProps={{
                                                        endAdornment: (
                                                            <InputAdornment position="end">
                                                                <IconButton
                                                                    onClick={() =>
                                                                        setShowConfirmPassword(
                                                                            (v) => !v
                                                                        )
                                                                    }
                                                                >
                                                                    <ShowPasswordIcon />
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    css={styles.inputField}
                                                />
                                            )}
                                        />
                                    </Box>

                                    <Button
                                        type="submit"
                                        fullWidth
                                        loading={isUpdatingPassword}
                                        css={styles.button}
                                    >
                                        {t('supabase_password_reset_submit')}
                                    </Button>
                                </form>
                            </Box>
                        )}
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
};
